import AddressSearchField from '@components/forms/address-search-field/address-search-field';
import { Button, Field, Input, Stack, Text } from '@components/ui';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import { type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { ServiceAreaMap } from './service-area-map';
import type {
  AddressTextField,
  RadiusOption,
  ServiceAreaRecord,
} from './serviceAreaTypes';

const quietButtonSx = {
  color: 'text.secondary',
  bgcolor: 'transparent',
  '&:hover': {
    bgcolor: 'transparent',
    borderColor: 'text.secondary',
    color: 'text.primary',
  },
};

interface ServiceAreaCardProps {
  area: ServiceAreaRecord;
  index: number;
  canDelete: boolean;
  radiusOptions: RadiusOption[];
  onToggleEdit: (index: number) => void;
  onDelete: (index: number) => void;
  onStreetChange: (address: string, index: number) => void;
  onStreetSelect: (address: string, index: number) => void;
  onFieldChange: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number
  ) => void;
}

const ServiceAreaCard = ({
  area,
  index,
  canDelete,
  radiusOptions,
  onToggleEdit,
  onDelete,
  onStreetChange,
  onStreetSelect,
  onFieldChange,
}: ServiceAreaCardProps): JSX.Element => {
  const { t } = useTranslation();
  const textFields: { name: AddressTextField; label: string; value: string }[] =
    [
      { name: 'city', label: 'City', value: area.city || '' },
      { name: 'state', label: 'State', value: area.state || '' },
      { name: 'zip', label: 'Zip Code', value: area.zip || '' },
    ];

  return (
    <Box
      sx={{
        mb: 2,
        overflow: 'visible',
        borderRadius: '10px',
        border: 1,
        borderColor: 'divider',
        bgcolor: (theme) =>
          theme.palette.mode === 'dark' ? '#262a2e' : '#f7f8fa',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.75,
          py: 1.25,
          borderBottom: 1,
          borderColor: 'divider',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 1.25, md: 0 },
        }}
      >
        <Text variant="label" sx={{ fontSize: 13, fontWeight: 700 }}>
          Address # {index + 1}
        </Text>
        <Stack
          direction="row"
          align="center"
          gap="sm"
          sx={{ width: { xs: '100%', md: 'auto' } }}
        >
          {area.id && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => onToggleEdit(index)}
              sx={quietButtonSx}
            >
              {area.isReadonly ? 'Edit' : 'Cancel'}
            </Button>
          )}
          {canDelete && (
            <Button
              type="button"
              variant="outlined"
              size="sm"
              onClick={() => onDelete(index)}
              sx={{ color: 'text.secondary' }}
            >
              {t('common.delete')}
            </Button>
          )}
        </Stack>
      </Box>
      <Box sx={{ p: 1.75 }}>
        <Field label={t('profile.streetAddress')}>
          <AddressSearchField
            value={area.address1 ?? ''}
            onChange={(address: string) => onStreetChange(address, index)}
            onSelect={(address: string) => onStreetSelect(address, index)}
            placeholder={t('profile.addressPlaceholder')}
            disabled={area.isReadonly}
          />
        </Field>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '2fr 1.5fr 1fr' },
            columnGap: 2,
            rowGap: 0.5,
            mt: 0.5,
          }}
        >
          {textFields.map((field) => (
            <Field key={field.name} label={field.label}>
              <Input
                name={field.name}
                value={field.value}
                disabled={area.isReadonly}
                onChange={(event) => onFieldChange(event, index)}
              />
            </Field>
          ))}
        </Box>
        <Field label={t('profile.serviceArea')} sx={{ mt: 0.5 }}>
          <Input
            select
            name="radius"
            value={area.radius || 10}
            disabled={area.isReadonly}
            onChange={(event) => onFieldChange(event, index)}
          >
            {radiusOptions.map((radius) => (
              <MenuItem key={radius.value} value={radius.value}>
                {radius.key}
              </MenuItem>
            ))}
          </Input>
        </Field>
        {typeof area.latitude === 'number' &&
        typeof area.longitude === 'number' ? (
          <ServiceAreaMap
            latitude={area.latitude}
            longitude={area.longitude}
            radiusMiles={Number(area.radius ?? 10)}
          />
        ) : null}
      </Box>
    </Box>
  );
};

export default ServiceAreaCard;
